/**
 * Integrytas Group - Performance Diagnostics Engine (PDE)
 * Page: PDE.DASHBOARD
 *
 * Real-time system health & performance dashboard for administrators. Polls
 * pde_snapshot() on a configurable interval for the live "now" view, and
 * pde_history() for trend sparklines. All admin-gated server-side.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';

interface Snapshot {
  captured_at: string;
  db: {
    active_connections: number; total_connections: number; max_connections: number;
    connection_pct: number | null; cache_hit_ratio: number | null; commits: number;
    rollbacks: number; deadlocks: number; db_size_bytes: number; longest_query_sec: number; temp_bytes: number;
  };
  app: { errors_5m: number; errors_1h: number; fatal_5m: number; audit_writes_5m: number };
  tasks: { pending: number; running: number; failed: number };
  web: { page_load_avg_ms: number | null; ttfb_avg_ms: number | null; samples_5m: number };
  error_code?: string;
}
interface HistPoint { sampled_at: string; metric_value: number }

const INTERVALS = [5, 10, 30, 60];

function bytes(n: number | null | undefined): string {
  if (!n && n !== 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
const pct = (r: number | null | undefined) => (r === null || r === undefined ? '—' : `${(r * 100).toFixed(1)}%`);

function Sparkline({ data, color = '#00529B' }: { data: number[]; color?: string }) {
  const { t } = useTranslation();
  if (data.length < 2) return <span className="text-csm-2xs text-csm-watermark">{t('pde.no_trend_yet', 'no trend yet')}</span>;
  const w = 120, h = 28, min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * h}`).join(' ');
  return (
    <svg width={w} height={h} className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function Tile({ label, value, sub, danger }: { label: string; value: ReactNode; sub?: string; danger?: boolean }) {
  return (
    <div className="rounded border border-csm-border bg-white p-3">
      <div className="text-csm-2xs uppercase tracking-wider text-csm-ink-soft">{label}</div>
      <div className={`mt-1 text-csm-xl font-semibold ${danger ? 'text-csm-danger' : 'text-csm-ink'}`}>{value}</div>
      {sub && <div className="text-csm-2xs text-csm-watermark">{sub}</div>}
    </div>
  );
}

export interface PdeDashboardPageProps { session: AuthSession; }

export function PdeDashboardPage({ session }: PdeDashboardPageProps) {
  return (
    <PersonalizationProvider pageCode="PDE.DASHBOARD" session={session}>
      <PdeDashboardPageInner session={session} />
    </PersonalizationProvider>
  );
}

function PdeDashboardPageInner(_props: PdeDashboardPageProps) {
  const { t } = useTranslation();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [hist, setHist] = useState<Record<string, number[]>>({});
  const [intervalSec, setIntervalSec] = useState(10);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastAt, setLastAt] = useState<string | null>(null);
  const tick = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const { data, error: e } = await apiDb.rpc('pde_snapshot');
      if (e) throw new Error(e.message);
      const s = data as Snapshot;
      if (s?.error_code) throw new Error(s.error_code);
      setSnap(s); setError(null); setLastAt(new Date().toLocaleTimeString());

      // Refresh trend lines less often (every ~6 ticks).
      if (tick.current % 6 === 0) {
        const keys = ['db.cache_hit_ratio', 'db.connections.total', 'app.errors.5m'];
        const results = await Promise.all(keys.map(k => apiDb.rpc('pde_history', { p_metric_key: k, p_minutes: 60 })));
        const next: Record<string, number[]> = {};
        keys.forEach((k, i) => {
          const rows = (results[i].data ?? []) as HistPoint[];
          next[k] = rows.map(r => Number(r.metric_value));
        });
        setHist(next);
      }
      tick.current += 1;
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => { void refresh(); }, intervalSec * 1000);
    return () => clearInterval(id);
  }, [paused, intervalSec, refresh]);

  const db = snap?.db; const app = snap?.app; const tasks = snap?.tasks; const web = snap?.web;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('pde.title', 'System Diagnostics')}</span>
        <button onClick={() => void refresh()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('pde.refresh', 'Refresh')}</button>
        <button onClick={() => setPaused(p => !p)} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{paused ? t('pde.resume', 'Resume') : t('pde.pause', 'Pause')}</button>
        <label className="flex items-center gap-1 text-csm-xs text-csm-ink-soft">{t('pde.every', 'every')}
          <select value={intervalSec} onChange={e => setIntervalSec(Number(e.target.value))} className="h-7 rounded border border-csm-border px-1 text-csm-sm">
            {INTERVALS.map(s => <option key={s} value={s}>{s}s</option>)}
          </select>
        </label>
        {lastAt && <span className="text-csm-2xs text-csm-watermark">{t('pde.updated', 'updated')} {lastAt}{paused ? t('pde.paused_suffix', ' (paused)') : ''}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {error && (
          <ErrorBanner
            message={t('pde.load_failed', "Couldn't load diagnostics.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void refresh()}
            details_label={t('common.details', 'Details')}
          />
        )}
        {!snap && !error && <SkeletonRows rows={8} />}

        {db && (
          <>
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pde.section.database', 'Database')}</div>
            <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
              <Tile label={t('pde.tile.connections', 'Connections')} value={`${db.total_connections}/${db.max_connections}`} sub={`${db.active_connections} active · ${db.connection_pct ?? 0}%`} danger={(db.connection_pct ?? 0) > 80} />
              <Tile label={t('pde.tile.cache_hit_ratio', 'Cache Hit Ratio')} value={pct(db.cache_hit_ratio)} danger={(db.cache_hit_ratio ?? 1) < 0.9} />
              <Tile label={t('pde.tile.database_size', 'Database Size')} value={bytes(db.db_size_bytes)} />
              <Tile label={t('pde.tile.longest_query', 'Longest Query')} value={`${db.longest_query_sec}s`} danger={db.longest_query_sec > 30} />
              <Tile label={t('pde.tile.deadlocks', 'Deadlocks')} value={db.deadlocks} danger={db.deadlocks > 0} />
              <Tile label={t('pde.tile.commits', 'Commits')} value={db.commits} />
              <Tile label={t('pde.tile.rollbacks', 'Rollbacks')} value={db.rollbacks} danger={db.rollbacks > db.commits} />
              <Tile label={t('pde.tile.temp_bytes', 'Temp Bytes')} value={bytes(db.temp_bytes)} />
            </div>
          </>
        )}

        {app && tasks && (
          <>
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pde.section.application_health', 'Application Health')}</div>
            <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
              <Tile label={t('pde.tile.errors_5m', 'Errors (5m)')} value={app.errors_5m} danger={app.errors_5m > 0} />
              <Tile label={t('pde.tile.errors_1h', 'Errors (1h)')} value={app.errors_1h} />
              <Tile label={t('pde.tile.fatal_5m', 'Fatal (5m)')} value={app.fatal_5m} danger={app.fatal_5m > 0} />
              <Tile label={t('pde.tile.audit_writes_5m', 'Audit Writes (5m)')} value={app.audit_writes_5m} />
              <Tile label={t('pde.tile.tasks_pending', 'Tasks Pending')} value={tasks.pending} />
              <Tile label={t('pde.tile.tasks_running', 'Tasks Running')} value={tasks.running} />
              <Tile label={t('pde.tile.tasks_failed', 'Tasks Failed')} value={tasks.failed} danger={tasks.failed > 0} />
            </div>
          </>
        )}

        {web && (
          <>
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pde.section.client_performance', 'Client Performance (last 5m)')}</div>
            <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
              <Tile label={t('pde.tile.avg_page_load', 'Avg Page Load')} value={web.page_load_avg_ms !== null ? `${web.page_load_avg_ms} ms` : '—'} />
              <Tile label={t('pde.tile.avg_ttfb', 'Avg TTFB')} value={web.ttfb_avg_ms !== null ? `${web.ttfb_avg_ms} ms` : '—'} />
              <Tile label={t('pde.tile.vital_samples', 'Vital Samples')} value={web.samples_5m} />
            </div>
          </>
        )}

        <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pde.section.trends', 'Trends (last hour)')}</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <div className="rounded border border-csm-border bg-white p-3">
            <div className="text-csm-2xs uppercase tracking-wider text-csm-ink-soft">{t('pde.tile.cache_hit_ratio', 'Cache Hit Ratio')}</div>
            <Sparkline data={hist['db.cache_hit_ratio'] ?? []} />
          </div>
          <div className="rounded border border-csm-border bg-white p-3">
            <div className="text-csm-2xs uppercase tracking-wider text-csm-ink-soft">{t('pde.tile.connections', 'Connections')}</div>
            <Sparkline data={hist['db.connections.total'] ?? []} color="#047857" />
          </div>
          <div className="rounded border border-csm-border bg-white p-3">
            <div className="text-csm-2xs uppercase tracking-wider text-csm-ink-soft">{t('pde.tile.errors_5m', 'Errors (5m)')}</div>
            <Sparkline data={hist['app.errors.5m'] ?? []} color="#B91C1C" />
          </div>
        </div>
      </div>
    </div>
  );
}
